/** Helpers visuales del Dashboard PJUD (sin lib de charts). */

export interface PjudChartSlice {
  key: string;
  label?: string;
  count: number;
}

export interface DonutSegment {
  key: string;
  label: string;
  count: number;
  percent: number;
  /** Offset 0–100 for stroke-dasharray ring. */
  offset: number;
  length: number;
  color: string;
}

const CHART_COLORS = ['#4C8BF5', '#34A853', '#A142F4', '#FA7B17', '#FBBC04', '#9AA0A6', '#B06000', '#0B57D0'];

export function chartColor(index: number): string {
  return CHART_COLORS[index % CHART_COLORS.length];
}

export function buildDonutSegments(slices: PjudChartSlice[]): {
  total: number;
  segments: DonutSegment[];
} {
  const clean = (slices || []).filter((s) => (Number(s.count) || 0) > 0);
  const total = clean.reduce((acc, s) => acc + (Number(s.count) || 0), 0);
  if (!total) {
    return { total: 0, segments: [] };
  }
  let cursor = 0;
  const segments = clean.map((s, i) => {
    const count = Number(s.count) || 0;
    const length = (count / total) * 100;
    const seg: DonutSegment = {
      key: s.key,
      label: s.label || s.key,
      count,
      percent: Math.round((count / total) * 1000) / 10,
      offset: cursor,
      length,
      color: chartColor(i),
    };
    cursor += length;
    return seg;
  });
  return { total, segments };
}

export function barPercent(count: number, max: number): number {
  const c = Number(count) || 0;
  const m = Number(max) || 0;
  if (c <= 0 || m <= 0) return 0;
  return Math.max(2, Math.round((c / m) * 100));
}

export function maxCount(slices: PjudChartSlice[]): number {
  return (slices || []).reduce((m, s) => Math.max(m, Number(s.count) || 0), 0);
}

/** Label visible / tooltip de un bucket de barra (label del API o key). */
export function chartBucketLabel(row: { label?: string; key?: string } | null | undefined): string {
  return String(row?.label || row?.key || '').trim();
}

/** Estado de filtros del Dashboard (default = Todo / fechas vacías). */
export interface PjudDashboardFilters {
  competency: string;
  causeStatus: string;
  proceduralStage: string;
  admissionFrom: string;
  admissionTo: string;
}

export const PJUD_DASHBOARD_FILTER_DEFAULTS: PjudDashboardFilters = {
  competency: 'all',
  causeStatus: 'all',
  proceduralStage: 'all',
  admissionFrom: '',
  admissionTo: '',
};

export function isPjudDashboardFiltersDefault(f: PjudDashboardFilters): boolean {
  return (
    (!f.competency || f.competency === 'all') &&
    (!f.causeStatus || f.causeStatus === 'all') &&
    (!f.proceduralStage || f.proceduralStage === 'all') &&
    !String(f.admissionFrom || '').trim() &&
    !String(f.admissionTo || '').trim()
  );
}

/**
 * Omite null/vacío/`all`/`todos` (case-insensitive) al armar query de charts.
 * Compartido entre util (UI → params) y el servicio HTTP.
 */
export function isOmittedChartParam(value: string | number | null | undefined): boolean {
  if (value == null) return true;
  const s = String(value).trim();
  if (!s) return true;
  const lower = s.toLowerCase();
  return lower === 'all' || lower === 'todos';
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Día real de calendario en YYYY-MM-DD (rechaza 2025-02-30). */
export function isValidIsoCalendarDate(value: string | null | undefined): boolean {
  const s = String(value || '').trim();
  if (!ISO_DATE_RE.test(s)) return false;
  const [y, m, d] = s.split('-').map((n) => Number(n));
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

/**
 * Descarta fechas no calendáricas y, si Desde > Hasta (tipeo manual), intercambia
 * para que el modelo coincida con lo que se manda al API.
 */
export function normalizeAdmissionDateFilters(f: PjudDashboardFilters): PjudDashboardFilters {
  let from = String(f.admissionFrom || '').trim();
  let to = String(f.admissionTo || '').trim();
  if (from && !isValidIsoCalendarDate(from)) from = '';
  if (to && !isValidIsoCalendarDate(to)) to = '';
  if (from && to && from > to) {
    const tmp = from;
    from = to;
    to = tmp;
  }
  if (from === f.admissionFrom && to === f.admissionTo) return f;
  return { ...f, admissionFrom: from, admissionTo: to };
}

/**
 * Mensaje breve si la normalización cambió lo que el usuario tipeó
 * (evita que el input “se borre solo” sin explicación).
 */
export function admissionDateNormalizeHint(
  before: PjudDashboardFilters,
  after: PjudDashboardFilters
): string | null {
  const bFrom = String(before.admissionFrom || '').trim();
  const bTo = String(before.admissionTo || '').trim();
  const aFrom = String(after.admissionFrom || '').trim();
  const aTo = String(after.admissionTo || '').trim();
  if (bFrom === aFrom && bTo === aTo) return null;

  // Mutuamente excluyentes: normalize limpia inválidas antes del swap,
  // así que nunca hay "swap + inválida" a la vez.
  const clearedInvalid =
    (!!bFrom && !aFrom && !isValidIsoCalendarDate(bFrom)) ||
    (!!bTo && !aTo && !isValidIsoCalendarDate(bTo));
  const swapped = !!bFrom && !!bTo && bFrom === aTo && bTo === aFrom;

  if (swapped) {
    return 'Se intercambiaron Desde y Hasta para que el rango sea válido.';
  }
  if (clearedInvalid) {
    return 'Se descartó una fecha de ingreso inválida.';
  }
  return null;
}

/** Params HTTP para GET …/charts (omite “all” / vacíos). */
export function pjudChartsQueryParams(f: PjudDashboardFilters): {
  competency?: string;
  causeStatus?: string;
  proceduralStage?: string;
  admissionFrom?: string;
  admissionTo?: string;
} {
  const n = normalizeAdmissionDateFilters(f);
  const out: {
    competency?: string;
    causeStatus?: string;
    proceduralStage?: string;
    admissionFrom?: string;
    admissionTo?: string;
  } = {};
  if (!isOmittedChartParam(n.competency)) out.competency = String(n.competency).trim();
  if (!isOmittedChartParam(n.causeStatus)) out.causeStatus = String(n.causeStatus).trim();
  if (!isOmittedChartParam(n.proceduralStage)) out.proceduralStage = String(n.proceduralStage).trim();
  if (!isOmittedChartParam(n.admissionFrom)) out.admissionFrom = String(n.admissionFrom).trim();
  if (!isOmittedChartParam(n.admissionTo)) out.admissionTo = String(n.admissionTo).trim();
  return out;
}

/**
 * stroke-dashoffset cuando el <circle> ya tiene transform="rotate(-90 …)".
 * No sumar +25: eso es la técnica alternativa (sin rotate) y juntas doble-rotan el donut.
 */
export function donutStrokeOffset(segmentOffset: number): number {
  return -(Number(segmentOffset) || 0);
}
